'use client'
import { type IconDefinition } from '@fortawesome/fontawesome-common-types'
import {
  faBookOpen,
  faCircleUser,
  faComments,
  faHeart,
  faSearch,
} from '@fortawesome/pro-light-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { NavigationMenu } from 'radix-ui'
import { cva } from 'class-variance-authority'
import clsx from 'clsx'
import { useTranslations } from 'next-intl'
import { useEffect, type ComponentProps } from 'react'
import { usePathname } from 'next/navigation'
import * as React from 'react'
import Link from 'next/link'
import { cn } from 'utils/cn'
import { FirstVisitTooltip } from '../Travelmagazine/FirstVisitTooltip'
import { Logo } from '../Logo/Logo'
import { ContactMenu } from './ContactMenu'
import { Indicator } from './Indicator'
import { MobileMenu } from './MobileMenu'

const BREAKPOINT_MOBILE = 449

export const QuickAccessMenu = ({
  customMobileContent,
  isAuthenticated,
  customContent,
  onMenuItemClick,
}: {
  isAuthenticated?: boolean
  customMobileContent?: React.ReactNode
  customContent?: React.ReactNode
  onMenuItemClick?: (args: {
    clickedElement?: string
    clickUrl?: string
  }) => void
}) => {
  const t = useTranslations('Navigation.QuickAccessMenu')
  const [value, setValue] = React.useState('')

  useEffect(
    function preventBackGroundBodyScrollOnMobile() {
      if (value && window.innerWidth < BREAKPOINT_MOBILE) {
        document.body.style.overflow = 'hidden'
      } else {
        document.body.style.overflow = ''
      }
      return () => {
        document.body.style.overflow = ''
      }
    },
    [value],
  )

  return (
    <NavigationMenu.Root
      className='relative -mb-[1px] flex w-full max-w-[calc(theme(screens.content)+120px)] px-4 md:px-[60px] [&>div]:w-full'
      onPointerLeave={(e) => e.preventDefault()}
      onValueChange={setValue}
      value={value}
    >
      <NavigationMenu.List className='text-body-xs-bold ml-auto flex w-full justify-between gap-2 lg:gap-6'>
        <NavigationMenu.Item className='mr-auto list-none'>
          <NavigationMenu.Link asChild>
            <Link className='shrink-0' href='/'>
              <Logo />
            </Link>
          </NavigationMenu.Link>
        </NavigationMenu.Item>
        {customContent}
        {customMobileContent ? (
          <ContentMenuItem
            className='lg:hidden'
            icon={faSearch}
            title={t('items.7.title')}
            triggerClass='lg:hidden'
          >
            {customMobileContent}
          </ContentMenuItem>
        ) : null}
        <ContentMenuItem icon={faComments} title={t('items.0.title')}>
          <ContactMenu closeFn={() => setValue('')} />
        </ContentMenuItem>
        <LinkMenuItem
          className='hidden lg:list-item'
          icon={faBookOpen}
          path={t('items.1.path')}
          title={t('items.1.title')}
        />
        <LinkMenuItem
          className='hidden lg:list-item'
          icon={faHeart}
          path={t('items.2.path')}
          title={t('items.2.title')}
        />
        <UserIcon isAuthenticated={isAuthenticated} />
        <MobileMenu onMenuItemClick={onMenuItemClick} />
        <Indicator />
      </NavigationMenu.List>
      <div className='perspective-[2000px] absolute left-0 top-full z-20 flex w-full'>
        <NavigationMenu.Viewport className='@container/viewport xs:max-w-[350px] bg-surface-primary shadow-menu data-[state=open]:animate-scaleIn data-[state=closed]:animate-scaleOut w-[var(--radix-navigation-menu-viewport-width] h-screen-without-header absolute right-0 w-full origin-[top_center] rounded-md sm:max-w-[500px] md:w-[var(--radix-navigation-menu-viewport-width)] md:min-w-[500px] lg:right-[10%] lg:mt-2.5 lg:h-[var(--radix-navigation-menu-viewport-height)] lg:min-h-[var(--radix-navigation-menu-viewport-height)] lg:min-w-min xl:right-[14%] 2xl:right-[18%]' />
      </div>
    </NavigationMenu.Root>
  )
}

export const UserIcon = ({
  isAuthenticated,
}: {
  isAuthenticated?: boolean
}) => {
  const t = useTranslations('Navigation.QuickAccessMenu')

  if (isAuthenticated) {
    return (
      <LinkMenuItem
        icon={faCircleUser}
        path={t('items.3.path')}
        title={t('items.3.title')}
      />
    )
  }
  return (
    <LinkMenuItem
      icon={faCircleUser}
      path={t('items.4.path')}
      title={t('items.4.title')}
    />
  )
}

export const menuItem = cva(
  'hover:text-primary-700 data-[state=open]:text-primary-700 data-[state=open]:border-primary-700 active:text-primary-800 flex h-[theme(height.header-mobile)] min-w-11 flex-col items-center justify-center gap-1 whitespace-nowrap border-b-[3px] font-bold *:translate-y-[3px] lg:h-[theme(height.header-desktop)]',
  {
    variants: {
      selected: {
        true: 'border-primary-700 text-primary-700 border-primary-800 border-b-[3px] hover:border-transparent',
        false: 'text-text-800 border-transparent',
      },
    },
    defaultVariants: { selected: false },
  },
)

const LinkMenuItem = ({
  icon,
  path,
  title,
  ...props
}: ComponentProps<typeof NavigationMenu.Item> & {
  icon: IconDefinition
  path: string
  title: string
  activePaths?: string[]
}) => {
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- Beim Build der Widgets ist usePathname() = null
  const currentPath = usePathname() ?? '/'
  const selected = currentPath.includes(path)

  return (
    <NavigationMenu.Item {...props}>
      <NavigationMenu.Link asChild className={clsx(menuItem({ selected }))}>
        <Link href={path}>
          <FontAwesomeIcon icon={icon} size='2xl' />
          <span className='max-lg:sr-only'>{title}</span>
        </Link>
      </NavigationMenu.Link>
    </NavigationMenu.Item>
  )
}

const ContentMenuItem = ({
  children,
  title,
  icon,
  firstVisitTooltip,
  triggerClass,
  ...props
}: ComponentProps<typeof NavigationMenu.Item> & {
  icon: IconDefinition
  hover?: boolean
  triggerClass?: string
  firstVisitTooltip?: boolean
}) => {
  return (
    <NavigationMenu.Item {...props}>
      {firstVisitTooltip ? (
        <FirstVisitTooltip>
          <NavigationMenu.Trigger
            className={clsx(menuItem(), triggerClass)}
            onPointerDown={(e) => {
              e.preventDefault()
              e.stopPropagation()
            }}
          >
            <FontAwesomeIcon icon={icon} size='2xl' />
            <span className='max-lg:sr-only'>{title}</span>
          </NavigationMenu.Trigger>
        </FirstVisitTooltip>
      ) : (
        <NavigationMenu.Trigger
          className={clsx(menuItem(), triggerClass)}
          onPointerDown={(e) => {
            e.preventDefault()
            e.stopPropagation()
          }}
        >
          <FontAwesomeIcon icon={icon} size='2xl' />
          <span className='max-lg:sr-only'>{title}</span>
        </NavigationMenu.Trigger>
      )}
      <NavigationMenu.Content
        className={cn(
          'data-[motion=from-start]:animate-enterFromLeft data-[motion=from-end]:animate-enterFromRight data-[motion=to-start]:animate-exitToLeft data-[motion=to-end]:animate-exitToRight absolute left-0 top-0 w-full sm:w-auto',
        )}
      >
        {children}
      </NavigationMenu.Content>
    </NavigationMenu.Item>
  )
}
